import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContractor } from 'context/ContractorContext';
import { getContractorBankAccounts } from 'services/contractorPortal';
import PlaidLinkButton from 'components/PlaidLinkButton';
import { createNotification } from 'services/timeOff';

const CARD_STYLE = {
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '16px 20px',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
};

const ContractorBankAccount = () => {
    const { contractorId } = useContractor();
    const companyId = localStorage.getItem('companyId') || '';
    const queryClient = useQueryClient();
    const [plaidError, setPlaidError] = useState('');
    const [addSuccess, setAddSuccess] = useState(false);
    const [plaidKey, setPlaidKey] = useState(0);

    const { data: accounts = [], isLoading, refetch } = useQuery({
        queryKey: ['cp-bank-accounts', contractorId],
        queryFn: () => getContractorBankAccounts(contractorId),
        enabled: !!contractorId,
    });

    const refresh = () => {
        setAddSuccess(true);
        setPlaidKey(k => k + 1);
        refetch();
        setTimeout(() => setAddSuccess(false), 4000);
        // Notify company admin of direct deposit change (security alert)
        if (companyId) {
            createNotification({
                recipientId: companyId,
                recipientType: 'company',
                type: 'profile_update',
                title: 'Contractor Direct Deposit Updated',
                message: 'A contractor connected or updated their bank account for direct deposit. Please review if unexpected.',
                link: `/contractor/view?code=${contractorId}&tab=bank-accounts`,
                companyId,
            }).catch(() => {});
        }
    };

    return (
        <div style={{ padding: '24px', backgroundColor: '#F8F9FA', minHeight: '100vh' }}>
            <h4 style={{ color: '#1a9fbb', fontWeight: 700, marginBottom: 4 }}>Bank Account</h4>
            <p style={{ color: '#6c757d', marginBottom: 24, fontSize: 14 }}>
                Connect your bank account to receive payments via direct deposit. Your account is securely verified through Plaid.
            </p>

            {addSuccess && (
                <div style={{ background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#155724', fontSize: 14, fontWeight: 500 }}>
                    Bank account connected successfully. It will appear below once verified.
                </div>
            )}

            {plaidError && (
                <div style={{ background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#721c24', fontSize: 14 }}>
                    {plaidError}
                </div>
            )}

            {/* Add new via Plaid */}
            <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                        <p style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>Connect via Plaid <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 11, fontWeight: 600, borderRadius: 4, padding: '2px 8px', marginLeft: 6 }}>Recommended</span></p>
                        <p style={{ color: '#6b7280', fontSize: 13, margin: '4px 0 0' }}>
                            Securely link your bank account to receive contractor payments. Powered by Plaid — your credentials are never shared with us.
                        </p>
                    </div>
                    <PlaidLinkButton
                        key={plaidKey}
                        userId={contractorId}
                        ownerType="contractor"
                        companyId={companyId}
                        label={accounts.length > 0 ? 'Update Bank Account' : 'Connect Bank Account'}
                        onSuccess={refresh}
                        onError={(msg) => setPlaidError(msg || 'Failed to connect bank account.')}
                    />
                </div>
            </div>

            {/* Accounts list */}
            <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <h6 style={{ fontWeight: 700, marginBottom: 16, fontSize: 15 }}>Accounts on File</h6>

                {isLoading ? (
                    <p style={{ color: '#6b7280', fontSize: 14 }}>Loading...</p>
                ) : accounts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af' }}>
                        <svg viewBox="0 0 24 24" fill="none" width="40" height="40" style={{ marginBottom: 12, opacity: 0.4 }}>
                            <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
                            <line x1="2" y1="10" x2="22" y2="10" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                        <p style={{ fontSize: 14, margin: 0 }}>No bank accounts on file. Connect one above to receive payments via direct deposit.</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {accounts.map((acct, idx) => (
                            <div key={acct.id ?? idx} style={CARD_STYLE}>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                                        {acct.institutionName || acct.accountName || 'Bank Account'}
                                        &nbsp;
                                        <span style={{ fontSize: 12, fontWeight: 400, color: '#6b7280', textTransform: 'capitalize' }}>
                                            ({acct.subtype || acct.accountType || 'checking'})
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                                        ••••{String(acct.accountNumber ?? '').slice(-4)}
                                        {acct.routingNumber && <>&nbsp;&nbsp;|&nbsp;&nbsp;Routing: {acct.routingNumber}</>}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    {acct.isVerified ? (
                                        <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 700 }}>
                                            ✓ Verified
                                        </span>
                                    ) : (
                                        <>
                                            <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 600 }}>
                                                Pending Verification
                                            </span>
                                            <PlaidLinkButton
                                                userId={contractorId}
                                                bankAccountId={acct.id}
                                                ownerType="contractor"
                                                companyId={companyId}
                                                label="Verify"
                                                onSuccess={refresh}
                                                onError={(msg) => setPlaidError(msg || 'Verification failed.')}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 16 }}>
                Secured by Plaid — your banking credentials are encrypted and never stored on our servers.
            </p>
        </div>
    );
};

export default ContractorBankAccount;
